import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, PawPrint, ArrowRight, Scissors } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useUpcomingBookings } from '../../hooks/useUpcomingBookings';
import { useNativeLifecycle } from '../../hooks/useNativeLifecycle';
import { useAnimals } from '../../hooks/useAnimals';
import NativeFirstAnimalPrompt from '../../components/native/NativeFirstAnimalPrompt/NativeFirstAnimalPrompt';
import {
  consumeFirstAnimalPromptPending,
  isFirstAnimalPromptDismissed,
} from '../../utils/nativeFirstAnimalPrompt';
import HomeNativeSearch, {
  type HomeNativeSearchHandle,
} from '../../components/native/HomeNativeSearch/HomeNativeSearch';
import UpcomingBookingCard from '../../components/native/UpcomingBookingCard/UpcomingBookingCard';
import './HomeNative.css';

/**
 * Home dédiée Capacitor — style app native.
 * Web → HomeClassic.tsx inchangé.
 */
const HomeNative: React.FC = () => {
  const { t } = useTranslation('native', { keyPrefix: 'home' });
  const { isAuthenticated, user } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const favoritesOn = isFeatureEnabled(FeatureFlag.FAVORITES);
  const navigate = useNavigate();
  const searchRef = useRef<HomeNativeSearchHandle>(null);
  const token = localStorage.getItem('token') ?? undefined;
  const [showFirstAnimalPrompt, setShowFirstAnimalPrompt] = useState(false);
  const { bookings: upcomingBookings, loading: bookingsLoading, refetch } = useUpcomingBookings(
    user?.user_id,
    3,
  );
  const { animals, loading: animalsLoading } = useAnimals(user?.user_id, token);

  useNativeLifecycle({ onResume: refetch });

  useEffect(() => {
    if (!isAuthenticated || !user?.user_id || animalsLoading) return;
    if (animals.length > 0) return;
    if (isFirstAnimalPromptDismissed(user.user_id)) return;
    if (!consumeFirstAnimalPromptPending()) return;
    setShowFirstAnimalPrompt(true);
  }, [isAuthenticated, user?.user_id, animals.length, animalsLoading]);

  const handleCreateFirstAnimal = () => {
    setShowFirstAnimalPrompt(false);
    navigate('/animal?add=1', { state: { fromHome: true } });
  };

  const focusSearch = () => {
    searchRef.current?.focusQuoi();
    document.getElementById('home-native-quoi')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="home-native">
      <div className="home-native__search-wrap">
        <HomeNativeSearch ref={searchRef} />
      </div>

      {isAuthenticated && user ? (
        <>
          <div className="home-native__quick">
            <button className="home-native__quick-item" onClick={focusSearch}>
              <div className="home-native__quick-icon home-native__quick-icon--brown">
                <MapPin size={20} />
              </div>
              <span>{t('findPro')}</span>
            </button>
            <button
              className="home-native__quick-item"
              onClick={() => navigate('/reservations')}
            >
              <div className="home-native__quick-icon home-native__quick-icon--gold">
                <Calendar size={20} />
              </div>
              <span>{t('myAppointments')}</span>
            </button>
            <button
              className="home-native__quick-item"
              onClick={() => navigate('/profile', { state: { profileTab: 'animaux' } })}
            >
              <div className="home-native__quick-icon home-native__quick-icon--peach">
                <PawPrint size={20} />
              </div>
              <span>{t('myAnimals')}</span>
            </button>
            {favoritesOn && (
              <button
                className="home-native__quick-item"
                onClick={() => navigate('/profile', { state: { profileTab: 'favoris' } })}
              >
                <div className="home-native__quick-icon home-native__quick-icon--warm">
                  <Scissors size={20} />
                </div>
                <span>{t('myFavorites')}</span>
              </button>
            )}
          </div>

          <section className="home-native__section">
            <div className="home-native__section-header">
              <h2 className="home-native__section-title">{t('upcomingTitle')}</h2>
              <button
                className="home-native__section-link"
                onClick={() => navigate('/reservations')}
              >
                {t('seeAll')} <ArrowRight size={14} />
              </button>
            </div>

            {bookingsLoading ? (
              <div className="home-native__skeleton">
                <div className="home-native__skeleton-item" />
                <div className="home-native__skeleton-item" />
              </div>
            ) : upcomingBookings.length > 0 ? (
              <div className="home-native__rdv-list">
                {upcomingBookings.map((b) => (
                  <UpcomingBookingCard key={b.bookingId} booking={b} />
                ))}
              </div>
            ) : (
              <div className="home-native__rdv-empty">
                <Calendar size={32} className="home-native__rdv-empty-icon" />
                <p>{t('noUpcoming')}</p>
                <button
                  className="home-native__cta home-native__cta--small"
                  onClick={focusSearch}
                >
                  {t('bookAppointment')}
                </button>
              </div>
            )}
          </section>
        </>
      ) : (
        <div className="home-native__unauthenticated">
          <div className="home-native__welcome">
            <h1 className="home-native__welcome-title">{t('greeting')}</h1>
            <p className="home-native__welcome-text">
              {t('welcomeText')}
            </p>
          </div>
          <button
            className="home-native__cta"
            onClick={() => navigate('/login')}
          >
            {t('signIn')}
          </button>
          <button
            className="home-native__cta home-native__cta--secondary"
            onClick={() => navigate('/signup')}
          >
            {t('signUp')}
          </button>
          <button
            type="button"
            className="home-native__settings-link"
            onClick={() => navigate('/profile/settings')}
          >
            {t('settingsLink')}
          </button>
        </div>
      )}

      {user?.user_id ? (
        <NativeFirstAnimalPrompt
          open={showFirstAnimalPrompt}
          userId={user.user_id}
          onClose={() => setShowFirstAnimalPrompt(false)}
          onCreate={handleCreateFirstAnimal}
        />
      ) : null}
    </div>
  );
};

export default HomeNative;
